import React, { useEffect, useState, useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { CheckCircle2, Loader2, Phone, ArrowLeft, ShieldCheck, Calendar, Clock } from "lucide-react";
import { api, formatApiError } from "../lib/api";
import Logo from "../components/Logo";

const SERVICE_LABELS_DE = {
    privat_haushalt: "Wohnungsreinigung",
    buero: "Büro Reinigung",
    gebaeude: "Gebäude Reinigung",
    scheiben: "Scheiben & Schaufenster",
    begleitservice: "Begleitservice / Alltagshilfe",
};

export default function OfferteAccept() {
    const { id } = useParams();
    const [params] = useSearchParams();
    const token = params.get("token") || "";
    const [offerte, setOfferte] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess] = useState(null);

    const [acceptedDate, setAcceptedDate] = useState("");
    const [acceptedTime, setAcceptedTime] = useState("");
    const [note, setNote] = useState("");

    useEffect(() => {
        const load = async () => {
            try {
                const { data } = await api.get(`/offerten/public/${id}`, { params: { token } });
                setOfferte(data);
                setAcceptedDate(data.proposed_date || "");
                setAcceptedTime(data.proposed_time || "");
            } catch (err) { setError(formatApiError(err)); }
            finally { setLoading(false); }
        };
        if (id && token) load();
        else { setLoading(false); setError("Link ungültig — bitte nutzen Sie den Link aus Ihrer E-Mail."); }
    }, [id, token]);

    const onAccept = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            const { data } = await api.post(`/offerten/public/${id}/accept`, {
                token,
                accepted_date: acceptedDate,
                accepted_time: acceptedTime || null,
                customer_note: note || null,
            });
            setSuccess(data.message || "Termin bestätigt.");
        } catch (err) { setError(formatApiError(err)); }
        finally { setSubmitting(false); }
    };

    const dateDiffersFromProposed = useMemo(() => {
        if (!offerte) return false;
        return (offerte.proposed_date && offerte.proposed_date !== acceptedDate) ||
               ((offerte.proposed_time || "") !== (acceptedTime || ""));
    }, [offerte, acceptedDate, acceptedTime]);

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]" data-testid="offerte-loading">
                <Loader2 className="w-8 h-8 animate-spin text-[#16A34A]" />
            </div>
        );
    }

    if (error && !offerte) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC] px-6" data-testid="offerte-error">
                <div className="max-w-md text-center">
                    <div className="inline-flex w-14 h-14 rounded-full bg-red-100 text-red-600 items-center justify-center mb-4">!</div>
                    <h1 className="font-display font-bold text-2xl text-[#1A365D] mb-2">Link ungültig</h1>
                    <p className="text-[#64748B] mb-6">{error}</p>
                    <Link to="/" className="btn-secondary" data-testid="offerte-error-home-link">Zur Startseite</Link>
                </div>
            </div>
        );
    }

    const alreadyAccepted = offerte?.status === "accepted" && !success;

    return (
        <div className="min-h-screen bg-[#F8FAFC] py-10 px-4" data-testid="offerte-accept-page">
            <div className="max-w-2xl mx-auto">
                <div className="flex items-center justify-between mb-6">
                    <Link to="/" className="inline-flex items-center" data-testid="offerte-home-link"><Logo variant="light" size="sm" /></Link>
                    <a href="tel:0786030294" className="text-sm font-semibold text-[#1A365D] inline-flex items-center gap-1.5" data-testid="offerte-call-link">
                        <Phone className="w-4 h-4 text-[#16A34A]" /> 078 603 02 94
                    </a>
                </div>

                {success ? (
                    <div className="rounded-3xl bg-white border border-[#16A34A]/30 shadow-lg p-10 text-center" data-testid="offerte-success-card">
                        <div className="inline-flex w-16 h-16 rounded-full bg-[#16A34A] text-white items-center justify-center mb-5">
                            <CheckCircle2 className="w-8 h-8" />
                        </div>
                        <h2 className="font-display font-bold text-2xl text-[#1A365D] mb-3">Vielen Dank — Termin fix verplant!</h2>
                        <p className="text-[#64748B] mb-6">{success}</p>
                        <div className="rounded-2xl bg-[#F0FDF4] border border-[#16A34A]/20 p-5 inline-block text-left">
                            <div className="text-xs uppercase tracking-wider font-bold text-[#16A34A] mb-1">Ihr bestätigter Termin</div>
                            <div className="font-display font-bold text-xl text-[#1A365D]">
                                {acceptedDate}{acceptedTime ? ` · ${acceptedTime}` : ""}
                            </div>
                        </div>
                        <p className="text-sm text-[#64748B] mt-6">Sie erhalten eine Bestätigung per E-Mail. Bei Fragen rufen Sie uns gerne an.</p>
                        <Link to="/" className="btn-secondary mt-6 inline-flex" data-testid="offerte-back-home-link"><ArrowLeft className="w-4 h-4" /> Zur Startseite</Link>
                    </div>
                ) : (
                    <div className="rounded-3xl bg-white border border-slate-200 shadow-lg p-7 md:p-10">
                        <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-[#16A34A] mb-3">
                            <ShieldCheck className="w-4 h-4" /> Offerte {offerte.offerte_number}
                        </div>
                        <h1 className="font-display font-bold text-3xl text-[#1A365D] tracking-tight mb-2">
                            Hallo {offerte.customer_name}, hier ist Ihre Offerte
                        </h1>
                        <p className="text-[#64748B] mb-7">Prüfen Sie die Eckdaten und bestätigen Sie unten den Termin — danach ist Ihr Auftrag fest verplant.</p>

                        {/* Summary */}
                        <div className="rounded-2xl bg-[#F8FAFC] border border-slate-200 p-5 mb-6">
                            <div className="text-xs uppercase tracking-wider font-bold text-[#1A365D]/70 mb-1">Leistung</div>
                            <div className="font-display font-bold text-lg text-[#1A365D] mb-4">{SERVICE_LABELS_DE[offerte.service_type] || offerte.service_label}</div>
                            <div className="flex justify-between items-baseline pt-4 border-t border-slate-200">
                                <span className="text-sm text-[#64748B]">Total inkl. MwSt.</span>
                                <span className="font-display font-black text-3xl text-[#16A34A]">CHF {Number(offerte.total_chf || 0).toFixed(2)}</span>
                            </div>
                            <div className="text-xs text-[#64748B] mt-2">Gültig bis {offerte.valid_until}</div>
                            {offerte.notes && <div className="mt-3 pt-3 border-t border-slate-200 text-sm text-[#0F172A] italic">{offerte.notes}</div>}
                        </div>

                        {alreadyAccepted ? (
                            <div className="rounded-2xl bg-[#F0FDF4] border border-[#16A34A]/30 p-6 text-center" data-testid="offerte-already-accepted">
                                <CheckCircle2 className="w-10 h-10 text-[#16A34A] mx-auto mb-3" />
                                <div className="font-display font-bold text-xl text-[#1A365D] mb-1">Diese Offerte wurde bereits angenommen.</div>
                                <div className="text-sm text-[#64748B]">Bestätigter Termin: <strong>{offerte.accepted_date}{offerte.accepted_time ? ` · ${offerte.accepted_time}` : ""}</strong></div>
                            </div>
                        ) : (
                            <form onSubmit={onAccept} className="space-y-5" data-testid="offerte-accept-form">
                                <div className="rounded-2xl bg-[#FFFBEB] border border-amber-200 p-5">
                                    <div className="text-xs uppercase tracking-wider font-bold text-amber-700 mb-3">Terminvorschlag — bitte bestätigen oder anpassen</div>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="text-xs font-semibold uppercase tracking-wider text-[#1A365D]/70 mb-1.5 inline-flex items-center gap-1.5"><Calendar className="w-3.5 h-3.5" /> Datum</label>
                                            <input required type="date" value={acceptedDate} onChange={(e) => setAcceptedDate(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm" data-testid="offerte-date-input" />
                                        </div>
                                        <div>
                                            <label className="text-xs font-semibold uppercase tracking-wider text-[#1A365D]/70 mb-1.5 inline-flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> Uhrzeit</label>
                                            <input type="time" value={acceptedTime} onChange={(e) => setAcceptedTime(e.target.value)} className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm" data-testid="offerte-time-input" />
                                        </div>
                                    </div>
                                    {dateDiffersFromProposed && (
                                        <div className="mt-3 text-xs text-amber-700">ℹ️ Sie schlagen einen anderen Termin als ursprünglich vorgeschlagen vor — wir nehmen das gerne entgegen.</div>
                                    )}
                                </div>

                                <div>
                                    <label className="text-xs font-semibold uppercase tracking-wider text-[#1A365D]/70 mb-1.5 block">Notiz an uns (optional)</label>
                                    <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} placeholder="z.B. Klingel funktioniert nicht, bitte anrufen" className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm" data-testid="offerte-note-input" />
                                </div>

                                {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-xl px-4 py-3" data-testid="offerte-form-error">{error}</div>}

                                <button type="submit" disabled={submitting || !acceptedDate} className="btn-primary w-full" data-testid="offerte-accept-button">
                                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                                    Offerte annehmen & Termin fix verplanen
                                </button>
                                <p className="text-xs text-[#64748B] text-center">Mit dem Klick bestätigen Sie die Annahme der Offerte. Es entstehen keine zusätzlichen Kosten.</p>
                            </form>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
